import { ErrorBoundary, GRichText, LogoLoader } from "@/components"
import { Facets, MobileFacets } from "@/components/Lister/Facets"
import { Products } from "@/components/Lister/Products"
import { SliceRender } from "@/components/Lister/Slice"
import { Sorting } from "@/components/Lister/Sorting"
import { useRobotsMetaTag } from "@/hooks/useRobotsMetaTag"
import { ListerDocument } from "@/lib/prismicio/types.generated"
import { useTextConversionMethod } from "@/lib/prismicio/useTextConversion"
import { useWishlist } from "@/lib/store/wishlist"
import {
    Container,
    Cross,
    Pagination,
    SliderFacetProvider,
    TopImage,
    useI18n,
    useLastLister,
    useLister,
} from "@jog/react-components"
import { getBaseCategoryFromPrettyPath } from "@jog/shared"
import { asText, isFilled } from "@prismicio/helpers"
import { lowerCase, upperFirst } from "lodash-es"
import { useEffect, useMemo } from "react"

import { Head } from "../Head/Head"
import { Filters } from "./Filters"

export const Lister = ({
    prismic,
    path: _path,
    keys,
    title,
    facets,
    paths,
}: {
    title: string
    prismic?: ListerDocument["data"]
    path: string
    keys: string[]
    facets: Record<string, string>
    paths: { locale: string; url: string }[]
}) => {
    const { $t } = useI18n()
    const textConvert = useTextConversionMethod()
    const wishlistLoading = useWishlist.use.actionPending()

    const categoryId = useMemo(
        () => keys.map((val) => `${process.env.NEXT_PUBLIC_TW_ROOT_CATEGORY_ID!.slice(0, 4)}${val}`).join("-") || "",
        [keys]
    )
    const { pending, response, total, loaded, getGroup, onChange, path, isRedirect, setIsRedirect } = useLister(
        _path,
        categoryId,
        facets
    )

    const filterPageSeoContent = useMemo(
        () =>
            [...(prismic?.filter_page_seo_content || [])]?.find(
                (item) => item.path === path && item.filter_page_seo_content_enabled
            ),
        [prismic, path]
    )
    const getRobotsMetaTag = useRobotsMetaTag(path)
    const meta = useMemo(() => {
        // Filter page metadata has precedence over base category metadata
        if (filterPageSeoContent) {
            return {
                title: asText(filterPageSeoContent?.filter_page_meta_tag_title),
                description: asText(filterPageSeoContent?.filter_page_meta_tag_description),
                robots: asText(filterPageSeoContent?.filter_page_meta_tag_robots) || getRobotsMetaTag,
            }
        }
        return {
            title: asText(prismic?.meta_tag_title),
            description: asText(prismic?.meta_tag_description),
            robots: asText(prismic?.meta_tag_robots) || getRobotsMetaTag,
        }
    }, [filterPageSeoContent, getRobotsMetaTag, prismic])
    const titleFromCt = textConvert(upperFirst(lowerCase(title)))
    const carouselBanners = [...(prismic?.body || [])].filter((i) => i.slice_type === "carousel_banners")
    const bottomDescription = useMemo(
        () => filterPageSeoContent?.filter_page_bottom_description || prismic?.bottom_description,
        [filterPageSeoContent?.filter_page_bottom_description, prismic?.bottom_description]
    )
    const { checkScroll } = useLastLister()
    useEffect(() => {
        if (response) {
            checkScroll(response.properties, response.items)
        }
    }, [checkScroll, response])
    return (
        <ErrorBoundary>
            <Head
                og={{ title: asText(prismic?.lister_title) || titleFromCt, description: meta.description }}
                alternate={paths}
                pathname={filterPageSeoContent?.path || getBaseCategoryFromPrettyPath(path).join("/")}
                title={textConvert(meta.title) || (titleFromCt ? `${titleFromCt} | GARCIA` : "GARCIA")}
                robots={meta.robots}
                description={meta.description}
            />
            {(wishlistLoading || pending) && <LogoLoader />}
            <Container>
                {isRedirect ? (
                    <div className="my-5 w-full px-2.5 lg:px-0">
                        <div className="flex items-start space-x-2 bg-grey-bg p-2.5 text-sm text-black">
                            <span className="flex-1">
                                {$t(
                                    "The product you were looking for is no longer available. But maybe you’ll like one of these!"
                                )}
                            </span>
                            <button
                                type="button"
                                aria-label={$t("Close")}
                                className="flex h-5 w-5 shrink-0 items-center justify-center"
                                onClick={() => setIsRedirect(false)}>
                                <Cross className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                ) : (
                    <></>
                )}
                <SliderFacetProvider onChange={onChange}>
                    <div className="grid grid-cols-12 gap-x-4">
                        <div className="col-span-12 hidden w-full lg:col-span-2 lg:block">
                            <Facets expand data={response} pending={pending} onChange={onChange} loaded={loaded} />
                        </div>
                        <div className="col-span-12 w-full lg:col-span-10">
                            {carouselBanners.length > 0 && <SliceRender slices={prismic.body} />}
                            <TopImage prismic={prismic} />
                            <div className="mx-0 mt-5 mb-3.75 lg:mt-0">
                                <h1 className="text-center font-primary text-sm font-medium uppercase leading-6 lg:text-left lg:text-13 lg:font-normal">
                                    {asText(prismic?.lister_title) || titleFromCt}
                                </h1>
                            </div>
                            {response?.properties?.sortfields?.length > 0 && (
                                <div className="mx-2.5 mt-4 mb-2.5 flex flex-col items-center bg-white lg:mx-1.75 lg:mt-0 lg:mb-4 lg:flex-row">
                                    <div className="flex w-full gap-x-2 lg:w-auto">
                                        <div className="flex-1 lg:hidden">
                                            <MobileFacets data={response} pending={pending} onChange={onChange} />
                                        </div>
                                        <div className="flex-1">
                                            <Sorting onChange={onChange} sortfields={response.properties.sortfields} />
                                        </div>
                                    </div>
                                    {total !== 0 && (
                                        <div className="w-full px-0 pt-2.5 pb-0 text-base lg:pt-0">
                                            <span>{`${total} ${$t(total > 1 ? "items" : "item")}`}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {response && <Filters data={response} onChange={(v) => onChange(v, true)} />}
                            <Products
                                getGroup={getGroup}
                                data={response}
                                loaded={loaded}
                                banners={[...(prismic?.content_banners || [])].slice(0, 2)}
                                category={_path}
                            />
                            {response && <Pagination data={response} onChange={(v) => onChange(v, true)} />}
                            {isFilled.richText(bottomDescription) ? (
                                <div className="mt-7.5 px-2">
                                    <GRichText field={bottomDescription} />
                                </div>
                            ) : null}
                        </div>
                    </div>
                </SliderFacetProvider>
            </Container>
        </ErrorBoundary>
    )
}
